import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';

const windows = [
  {
    id: 'none',
    label: 'Klart Glass',
    price: 'Inkludert',
    priceNote: 'Standard',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/c518b32ca_ClearGlass.png',
    description: 'Badstuen leveres uten ekstra vindu. Gir maksimal isolasjon og privatliv, med fullt fokus på varme og avslapping.',
    pros: ['Maksimal isolasjon', 'Fullstendig privatliv', 'Ingen varmetap', 'Lavest kostnad'],
    view: 'Ingen',
    light: 'Kun dør',
  },
  {
    id: 'small',
    label: 'Lett Tonert Glass',
    price: '+8 000 kr',
    priceNote: 'Tillegg',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/3c2b6b3f5_Black_Glass.png',
    description: 'Lett tonert glass som bringer inn naturlig lys og gir utsikt uten å påvirke varmebevaring vesentlig. Perfekt balanse mellom funksjon og estetikk.',
    pros: ['Naturlig lys fra siden', 'Utsikt med demping', 'Minimal varmetap', 'Stilren løsning'],
    view: 'Begrenset',
    light: 'Middels',
  },
  {
    id: 'large',
    label: 'Sterkt Tonert Glass',
    price: '+15 000 kr',
    priceNote: 'Premium',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/8984f902f_BrownGlass.png',
    description: 'Sterkt tonert glass som gir maksimal privatliv og reduserer dagslys betydelig. Ideelt når du ønsker full kontroll over lys og atmosfære i badstuen.',
    pros: ['Maksimal privatliv', 'Redusert dagslys', 'Full kontroll over atmosfære', 'Dyp, kontemplatativ opplevelse'],
    view: 'Minimalt',
    light: 'Dempet',
  },
];

export default function InnvendigVindu() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
        className="relative h-[500px] overflow-hidden">
        <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/8984f902f_BrownGlass.png"
          alt="Vindu" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/50"></div>
        <div className="absolute inset-0 flex flex-col justify-end p-12 z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-sm tracking-widest font-body uppercase text-white/60 mb-4">Innvendig materialer · Artikkel 4</p>
            <h1 className="font-heading text-6xl md:text-7xl font-light leading-[1.0] text-white mb-4">Vindu</h1>
            <p className="font-body text-white/80 text-xl max-w-2xl leading-relaxed">
              Fra fullstendig privatliv til panoramautsikt over naturen
            </p>
          </motion.div>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-16 max-w-3xl">
          <h2 className="font-heading text-4xl font-light text-[#2a1810] mb-6">Hvilke vinduer kan du velge?</h2>
          <p className="font-body text-lg text-foreground/70 leading-relaxed">
            Her er en kortfattet oversikt over vindualternativene du kan velge mellom. Fra ingen vindu til panoramautsikt over naturen.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {windows.map((w, idx) => (
            <motion.div key={w.id}
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: idx * 0.1 }}
              whileHover={{ y: -6 }}
              className="bg-white border border-border/20 rounded-[12px] overflow-hidden hover:shadow-xl hover:border-[#2d5a3d] transition-all">
              <div className="relative h-80 overflow-hidden bg-secondary/20 flex items-center justify-center">
                <img src={w.img} alt={w.label} className="h-full w-full object-contain p-4" />
                <div className="absolute top-4 right-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${w.priceNote === 'Standard' ? 'bg-[#2d5a3d] text-white' : w.priceNote === 'Premium' ? 'bg-[#2a1810] text-white' : 'bg-white/90 text-[#2a1810]'}`}>
                    {w.price}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl font-light text-[#2a1810] mb-3">{w.label}</h3>
                <p className="font-body text-foreground/70 leading-relaxed mb-4 text-base">{w.description}</p>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-secondary/30 rounded-[6px] p-3 text-center">
                    <p className="font-body text-xs text-foreground/50 uppercase tracking-widest">Utsikt</p>
                    <p className="font-body text-base font-medium text-[#2a1810]">{w.view}</p>
                  </div>
                  <div className="bg-secondary/30 rounded-[6px] p-3 text-center">
                    <p className="font-body text-xs text-foreground/50 uppercase tracking-widest">Lys</p>
                    <p className="font-body text-base font-medium text-[#2a1810]">{w.light}</p>
                  </div>
                </div>
                <div className="space-y-1">
                  {w.pros.map((pro, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check size={12} className="text-[#2d5a3d] flex-shrink-0" />
                      <span className="font-body text-base text-foreground/60">{pro}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-[#2d5a3d]/10 to-transparent border-l-4 border-[#2d5a3d] rounded-[8px] p-8 mb-12">
          <h3 className="font-heading text-xl font-light text-[#2a1810] mb-3">Tips: Vurder plasseringen</h3>
          <p className="font-body text-foreground/70 leading-relaxed">
            Plasser vinduet mot den vakreste utsikten — skog, sjø eller hage. Et panoramavindu vendende mot nord gir jevnt, dempet lys uten direkte sol. Mot vest gir det spektakulær sol om kvelden.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <a href="/innvendig-dorer" className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-body text-sm tracking-widest hover:bg-foreground hover:text-background transition-all rounded-[4px]">
            <ArrowLeft size={16} /> Forrige: Dører
          </a>
          <a href="/innvendig-belysning" className="inline-flex items-center gap-2 px-8 py-4 bg-[#2a1810] text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-[#2a1810]/90 transition-all">
            Neste: Belysning <ArrowRight size={16} />
          </a>
        </div>
      </div>
      <Footer />
    </div>
  );
}